import React from 'react';
import ContentLoader from 'react-content-loader';
import { themeColors } from 'src/utils/styles/global.style';
import './SkeletonLoader.css';

// set loading prop
type SkeletonProps = {
  loading: boolean;
  style?: any;
  innerWidth?: number;
  rowNumber?: number; //defines the number of rows
};

//background and foreground colors
const bgColor = themeColors.skeletonBackground;
const fgColor = themeColors.skeletonForeground;

//Place holder skeleton template for investor loading accordion in investor manager page
export const LoaderFileSkeleton: React.FC<SkeletonProps> = (props: SkeletonProps) => {
  if (!props.loading) return null;

  //else load investor skeleton place holder
  const rows = 2;
  const columns = 6;
  const coverHeight = 220;
  const coverWidth = 200;
  const padding = 20;
  const speed = 1;

  const coverHeightWithPadding = coverHeight + padding;
  const coverWidthWithPadding = coverWidth + padding;
  const initial = 35;
  const covers = Array(columns * rows).fill(1);

  return (
    <div>
      {/* </di>v style={{ margin: '1rem 11rem' }}> */}
      {/* for investor file */}
      {/* <div style={{ margin: '1rem 7rem' }}> */}
      {/* for media */}

      <ContentLoader
        speed={speed}
        width={columns * coverWidthWithPadding}
        height={rows * coverHeightWithPadding * 1.2}
        backgroundColor={bgColor}
        foregroundColor={fgColor}
        {...props}
      >
        {/* <rect
          x="0"
          y="0"
          rx="5"
          ry="5"
          width={columns * coverWidthWithPadding - padding}
          height="20"
        /> */}

        {covers.map((g, i) => {
          let vy = Math.floor(i / columns) * coverHeightWithPadding + initial;
          let vx = (i * coverWidthWithPadding) % (columns * coverWidthWithPadding);
          return (
            <rect key={i} x={vx} y={vy} rx="10" ry="10" width={coverWidth} height={coverHeight} />
          );
        })}
      </ContentLoader>
    </div>
  );
};

/**
 * Place holder for loading tables
 * @param props Skeleton props
 * @returns Skeleton format for loading tables
 */
export const LoaderTableSkeleton: React.FC<SkeletonProps> = (props: SkeletonProps) => {
  if (!props.loading) return null;

  const { rowNumber } = props;

  //else load investor skeleton place holder
  const rows = rowNumber ?? 5;
  const columns = 1;
  const coverHeight = 30;
  const coverWidth = 1088.52;
  const padding = 10;
  const speed = 3.2;

  const coverHeightWithPadding = coverHeight + padding;
  const coverWidthWithPadding = coverWidth + padding;
  const initial = 35;
  const covers = Array(columns * rows).fill(1);

  return (
    <div>
      <ContentLoader
        speed={speed}
        width={columns * coverWidthWithPadding}
        height={rows * coverHeightWithPadding * 1.2}
        backgroundColor={bgColor}
        foregroundColor={fgColor}
        {...props}
      >
        {/* <rect
          x="0"
          y="-5"
          rx="10"
          ry="10"
          width={coverWidth}
          height={coverHeight * 1.1}
        /> */}

        {covers.map((g, i) => {
          let vy = Math.floor(i / columns) * coverHeightWithPadding + initial;
          let vx = (i * coverWidthWithPadding) % (columns * coverWidthWithPadding);
          return (
            <rect key={i} x={vx} y={vy} rx="10" ry="10" width={coverWidth} height={coverHeight} />
          );
        })}
      </ContentLoader>
    </div>
  );
};

//Place holder skeleton template for investor loading accordion in investor manager page
export const InvestmentManagerInvestorSkeleton: React.FC<SkeletonProps> = (
  props: SkeletonProps,
) => {
  if (!props.loading) return null;
  const currWidth = props.innerWidth ?? window.innerWidth;
  //else load investor skeleton place holder
  const rows = 1;
  const columns = 5;
  const coverHeight = 58;
  const coverWidth = 295;
  const speed = 1;

  const coverSpace = 10;
  const spacedWidth = coverWidth + 15;
  const height = 300;
  const fullWidth = (coverSpace + coverWidth) * columns;
  const ratio = currWidth / 1920; //cannot be 0 division since initial is positive

  const covers = Array(columns * rows).fill(1);

  return (
    <div style={{ margin: '0 5px 0 5px' }}>
      <ContentLoader
        speed={speed}
        width={window.innerWidth}
        height={height}
        backgroundColor={bgColor}
        foregroundColor={fgColor}
        {...props}
      >
        {/* cards */}
        {covers.map((g, i) => {
          //make cover responsive
          const ratioWidthCover = coverWidth * ratio;

          //calc x y position
          let vy = Math.floor(i / columns) * ratioWidthCover + coverSpace * ratio;
          let vx = i * (ratioWidthCover + coverSpace);

          return (
            <rect
              key={i}
              x={vx}
              y={vy}
              rx="10"
              ry="10"
              width={coverWidth * ratio}
              height={coverHeight}
            />
          );
        })}

        {/* lines */}
        <rect x="0" y="115" rx="5" ry="5" width="780" height="15" />
        <rect x="0" y="145" rx="5" ry="5" width="700" height="12" />
        <rect x="0" y="168" rx="5" ry="5" width="620" height="10" />
      </ContentLoader>
    </div>
  );
};

// Props definition for the generic SkeletonLoader component
export interface SkeletonPropsDynamic {
  loading: boolean; // Determines whether the skeleton should be shown
  rowNumber?: number; // The number of rows to display
  columnNumber?: number; // The number of columns per row
  itemHeight: number; // The height of each skeleton item
  itemWidth: number; // The width of each skeleton item
  fullWidth?: boolean; // Determines whether the skeleton should be full width and ignore the itemWidth
  borderRadius?: number; // The border radius of each skeleton item
  style?: React.CSSProperties; // Additional styles to apply to the skeleton container
  children?: React.ReactNode; // The children to render
}

/**
 * SkeletonLoader is a generic loading placeholder component that can be used while content is loading.
 * @param {SkeletonPropsDynamic} props - The props for the component.
 * @returns {React.FC<SkeletonPropsDynamic>} - A functional component that returns a set of animated skeleton placeholders.
 */
export const SkeletonLoaderDynamic: React.FC<SkeletonPropsDynamic> = ({
  loading,
  rowNumber = 4, // Default to 4 rows if not specified
  columnNumber = 1, // Default to 1 column if not specified
  itemHeight,
  itemWidth,
  fullWidth = false, // Default to false if not specified
  borderRadius = 10, // Default border radius if not specified
  style = {}, // Default gap between items if not specified,
  children,
}) => {
  if (!loading) return <div>{children}</div>; // Don't render anything if not loading

  // Create an array of indexes for the skeleton items
  const skeletons = Array.from({ length: rowNumber * columnNumber }, (_, index) => index);

  // Render the skeleton items
  return (
    <div className="skeleton-container" style={style}>
      {skeletons.map(skeleton => (
        <div
          key={skeleton}
          className="skeleton-item"
          style={{
            width: fullWidth ? '100%' : `${itemWidth}px`,
            height: `${itemHeight}px`,
            borderRadius: `${borderRadius}px`,
          }}
        >
          <div className="skeleton-animation"></div>
        </div>
      ))}
    </div>
  );
};

export default SkeletonLoaderDynamic;
